import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

// Combina clases de Tailwind sin conflictos
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

// Formatea duración en segundos a mm:ss
export function formatearDuracion(segundos: number): string {
  const mins = Math.floor(segundos / 60)
  const segs = segundos % 60
  return `${mins}:${segs.toString().padStart(2, '0')}`
}

// Formatea fecha en español
export function formatearFecha(fechaISO: string): string {
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(fechaISO))
}

// Formatea fecha relativa (hace 2 min, ayer, etc.)
export function formatearFechaRelativa(fechaISO: string): string {
  const diff = Date.now() - new Date(fechaISO).getTime()
  const minutos = Math.floor(diff / 60000)
  const horas = Math.floor(diff / 3600000)
  const dias = Math.floor(diff / 86400000)

  if (minutos < 1) return 'ahora mismo'
  if (minutos < 60) return `hace ${minutos} min`
  if (horas < 24) return `hace ${horas}h`
  if (dias === 1) return 'ayer'
  if (dias < 7) return `hace ${dias} días`
  return formatearFecha(fechaISO)
}

// Extrae título del primer patrón encontrado en texto
export function extraerTituloReporte(contenido: string): string {
  const primeraLinea = contenido.split('\n').find(l => l.trim().length > 0)
  if (!primeraLinea) return 'Reporte sin título'
  return primeraLinea.replace(/^[#\-*]+\s*/, '').trim().slice(0, 60)
}

// Cuenta palabras en texto
export function contarPalabras(texto: string): number {
  return texto.trim().split(/\s+/).filter(Boolean).length
}

// Trunca texto preservando palabras completas
export function truncar(texto: string, maxChars: number): string {
  if (texto.length <= maxChars) return texto
  return texto.slice(0, maxChars).replace(/\s+\S*$/, '') + '…'
}

// Genera ID único de cliente
export function generarId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

// Espera N milisegundos
export function esperar(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Detecta si el navegador soporta Web Speech API
export function soportaVoz(): boolean {
  return 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window
}

// Copia texto al portapapeles
export async function copiarPortapapeles(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto)
    return true
  } catch {
    // Fallback para navegadores sin permisos
    const textarea = document.createElement('textarea')
    textarea.value = texto
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  }
}

// Detecta modalidad desde texto del patrón/comando
export function detectarModalidadDesdeTexto(texto: string): import('@/types').Modalidad | null {
  const t = texto.toLowerCase()
  if (t.includes('mamograf') || t.includes('mastograf') || t.includes('mamo') || (t.includes('mama') && t.includes('radiograf'))) return 'MAMO'
  if (t.includes('tórax') || t.includes('torax') || /\brx\b/.test(t) || t.includes('radiograf')) return 'RX'
  if (t.includes('fluoroscop') || t.includes('esofagograma')) return 'RX'
  if (t.includes('tomograf') || /\btac\b/.test(t) || /\btc\b/.test(t)) return 'TAC'
  if (t.includes('eco') || t.includes('ultrasono') || t.includes('ultra')) return 'ECO'
  if (t.includes('resonan') || /\brm\b/.test(t) || t.includes('mri')) return 'RM'
  return null
}
